/**
 * Topology banner — the GRID's answer above the indication form.
 *
 * REWRITTEN 2026-09-02 onto deriveTopology (grid-only, owner ruling):
 *  - ADD mode (`lngLat`, no indication): derivation at the click point,
 *    auto-applied ONCE into the form — creation-time fill is the one
 *    allowed auto-apply. No grid cell under the click ⇒ no banner, no
 *    fill (never a proximity guess).
 *  - EDIT mode (`indicationId`): the full plan vs stored values. NEVER
 *    auto-applies (write-on-view is banned); shows "grid says" chips for
 *    fields that are blank or disagree, with an explicit Apply that
 *    stages the derived values into the FORM (persisted on Save).
 *
 * Gone by design: the confidence % (it described evidence the old engine
 * ignored — T-13) and the "Nearest: <label> (Nm)" line (a proximity
 * artifact; the grid's answer has a cell, not a nearest label).
 */

import { useState, useEffect, useRef } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import {
  useDerivedTopologyPlan,
  useDerivedFieldsAt,
  useDerivedFieldsForGeometry,
} from '../../domain/topology/useDerivedTopology';
import { humanEditProvenance } from '../../domain/topology/applyDerivedTopology';
import type { TopologyFields } from '../../domain/layout/types';
import './TopologySuggestionBanner.css';

interface TopologySuggestionBannerProps {
  /** ADD mode: the click point (reset key + derive fallback) */
  lngLat: [number, number] | null;
  /** ADD mode: the rect that WILL exist at creation — preferred over the
   *  bare point so the popup and quick-add run the same derive (a String OC
   *  rect spans its string; the point sees one cell). */
  addGeometry?: GeoJSON.Geometry | null;
  /** ADD mode: the currently chosen indication type (String OC ⇒ string mode) */
  addType?: string;
  /** EDIT mode: the existing indication — plan runs against its geometry
   *  and stored values */
  indicationId?: string | null;
  onAccept: (fields: Partial<TopologyFields>, customFields?: Record<string, string>) => void;
  /** ADD mode only: fill the form as soon as the derivation arrives */
  autoApply?: boolean;
}

const FIELD_LABELS: Record<string, string> = {
  station: 'Station',
  inverter: 'Inverter',
  mppt: 'MPPT',
  dccb: 'DCCB',
  string: 'String',
  module: 'Module',
};

export default function TopologySuggestionBanner({
  lngLat,
  addGeometry,
  addType,
  indicationId,
  onAccept,
  autoApply = true,
}: TopologySuggestionBannerProps) {
  const indication = useProjectStore((s) =>
    indicationId ? s.data.indications.find((i) => i.id === indicationId) : undefined,
  );
  const plan = useDerivedTopologyPlan(indicationId ? indication : null);
  const atGeometry = useDerivedFieldsForGeometry(
    indicationId ? null : addGeometry ?? null,
    addType,
  );
  const atFallbackPoint = useDerivedFieldsAt(indicationId || addGeometry ? null : lngLat);
  const atPoint = atGeometry ?? atFallbackPoint;

  const [dismissed, setDismissed] = useState(false);
  const appliedRef = useRef(false);

  // ADD mode: creation-time fill, once per location
  useEffect(() => {
    if (indicationId) return; // edit mode NEVER auto-applies
    if (autoApply && atPoint && !dismissed && !appliedRef.current) {
      appliedRef.current = true;
      onAccept(atPoint.fields, atPoint.customFields);
    }
  }, [atPoint, autoApply, dismissed, indicationId, onAccept]);

  useEffect(() => {
    appliedRef.current = false;
    setDismissed(false);
  }, [lngLat?.[0], lngLat?.[1], indicationId]);

  if (dismissed) return null;

  // ── EDIT mode: chips for blank + disagreeing fields ──
  if (indicationId) {
    if (!plan || (plan.conflicts.length === 0 && !plan.updates)) return null;
    const entries: Array<{ key: string; value: string; conflict?: string }> = [];
    for (const c of plan.conflicts) {
      entries.push({ key: c.field, value: c.derived, conflict: c.stored });
    }
    if (plan.updates) {
      for (const [k, v] of Object.entries(plan.updates)) {
        if (k === 'topologyProvenance' || k === 'customFields' || typeof v !== 'string') continue;
        entries.push({ key: k, value: v });
      }
    }
    if (entries.length === 0) return null;
    const applyFields: Partial<TopologyFields> = {};
    for (const e of entries) applyFields[e.key as keyof TopologyFields] = e.value;
    return (
      <div className="topo-banner">
        <div className="topo-banner__header">
          <span className="topo-banner__icon">&#x2317;</span>
          <span className="topo-banner__title">Grid says</span>
        </div>
        <div className="topo-banner__fields">
          {entries.map((e) => (
            <span
              key={e.key}
              className={`topo-banner__chip${e.conflict !== undefined ? ' topo-banner__chip--conflict' : ''}`}
              title={e.conflict !== undefined ? `stored: ${e.conflict}` : 'stored value is empty'}
            >
              <span className="topo-banner__chip-label">{FIELD_LABELS[e.key] ?? e.key}:</span>
              <span className="topo-banner__chip-value">{e.value}</span>
            </span>
          ))}
          {plan.result.kind === 'grid' && plan.result.ambiguous && (
            <span className="topo-banner__flag" title="Two cells overlap this marker almost equally — the answer is a near-tie">≈ ambiguous</span>
          )}
          {plan.result.kind === 'grid' && !plan.result.isString && plan.result.significantCellCount > 2 && (
            <span className="topo-banner__flag" title={`${plan.result.significantCellCount} cells overlap this single-module marker — the grid likely has overlapping cells here`}>⧉ overlapping cells</span>
          )}
        </div>
        <div className="topo-banner__actions">
          <button
            type="button"
            className="topo-banner__btn topo-banner__btn--accept"
            onClick={() => onAccept(applyFields, plan.result.kind === 'grid' ? plan.result.customFields : undefined)}
          >
            Apply
          </button>
          {/* "Keep stored" (c6, 2026-09-03): ground-truthing "stored is
              right" stamps 'human' on the disputed fields so re-derives and
              the bulk accept tier never touch them — the sampling look and
              the protection are one gesture. Conflict chips then drop out
              of the plan (respectHuman); fill chips stay. */}
          {plan.conflicts.length > 0 && indication && (
            <button
              type="button"
              className="topo-banner__btn topo-banner__btn--keep"
              title="The stored values are right — protect them from re-derives and bulk runs"
              onClick={() => {
                useProjectStore.getState().updateIndication(indication.id, {
                  properties: humanEditProvenance(
                    indication.properties as unknown as Record<string, unknown>,
                    plan.conflicts.map((c) => c.field),
                    // the grid state this verdict was decided against —
                    // lets the suppression expire if the grid changes
                    plan.result.kind === 'grid' ? plan.result.source : undefined,
                  ),
                });
              }}
            >
              Keep stored
            </button>
          )}
          <button
            type="button"
            className="topo-banner__btn topo-banner__btn--dismiss"
            onClick={() => setDismissed(true)}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // ── ADD mode: the derivation that (auto-)fills the form ──
  if (!atPoint) return null;
  const fieldEntries = Object.entries(atPoint.fields) as [keyof TopologyFields, string][];
  const customEntries = atPoint.customFields ? Object.entries(atPoint.customFields) : [];
  if (fieldEntries.length === 0 && customEntries.length === 0) return null;

  return (
    <div className="topo-banner">
      <div className="topo-banner__header">
        <span className="topo-banner__icon">&#x2317;</span>
        <span className="topo-banner__title">Grid</span>
      </div>
      <div className="topo-banner__fields">
        {fieldEntries.map(([key, value]) => (
          <span key={key} className="topo-banner__chip">
            <span className="topo-banner__chip-label">{FIELD_LABELS[key]}:</span>
            <span className="topo-banner__chip-value">{value}</span>
          </span>
        ))}
        {customEntries.map(([key, value]) => (
          <span key={`c-${key}`} className="topo-banner__chip">
            <span className="topo-banner__chip-label">{key}:</span>
            <span className="topo-banner__chip-value">{value}</span>
          </span>
        ))}
      </div>
      <div className="topo-banner__actions">
        {!appliedRef.current && (
          <button
            type="button"
            className="topo-banner__btn topo-banner__btn--accept"
            onClick={() => {
              appliedRef.current = true;
              onAccept(atPoint.fields, atPoint.customFields);
            }}
          >
            Accept
          </button>
        )}
        <button
          type="button"
          className="topo-banner__btn topo-banner__btn--dismiss"
          onClick={() => setDismissed(true)}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
